import { OtaStatus } from './ota-status'
import { ErrorDetails } from '@w/ui/components/error-details'
import { useEffect, useRef, useState } from 'react'
import { isProd } from '@w/utils'
import { GoogleSignInButton, useGoogleSignIn } from '@w/auth-fe'
import { Button } from '@w/ui/components/button'
import { MailIcon, SettingsIcon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { Field, FieldControl, FieldError } from '@w/ui/components/field'
import { Form } from '@w/ui/components/form'
import { Input } from '@w/ui/components/input'
import { useMutation } from '@w/react-query'
import { useTranslation } from '../i18n'
import { feEnvVars } from '../fe-env-vars'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import { useNavigate } from '../router/flashcards-fe.router-bindings'
import { Image } from '@w/ui/components/image'

interface LoginPageProps {
  initialEmailSentTo?: string
}

export function LoginPage() {
  return <LoginPageContent />
}

export function LoginPageContent({ initialEmailSentTo }: LoginPageProps = {}) {
  const { t } = useTranslation('auth')
  const { auth, tel } = useRouterContext()
  const navigate = useNavigate()
  const localE2eGoogle =
    // Bundler define is absent when Bun imports this component directly.
    // oxlint-disable-next-line eslint-plugin-unicorn/no-typeof-undefined -- compile-time identifier can be unbound in tests
    typeof FLASHCARDS_LOCAL_E2E_GOOGLE === 'undefined'
      ? false
      : FLASHCARDS_LOCAL_E2E_GOOGLE
  const enableGoogleLogin = isProd || localE2eGoogle
  const [email, setEmail] = useState(initialEmailSentTo ?? '')
  const [showEmailForm, setShowEmailForm] = useState(
    initialEmailSentTo !== undefined,
  )
  const emailSubmitRef = useRef<HTMLButtonElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const shouldFocusEmailRef = useRef(false)
  const [authError, setAuthError] = useState<unknown>(null)
  const [emailSentTo, setEmailSentTo] = useState<string | null>(
    initialEmailSentTo ?? null,
  )
  const emailMagicLinkMutation = useMutation({
    mutationFn: (nextEmail: string) =>
      auth.signIn.magicLink({ email: nextEmail }),
  })
  const googleSignIn = useGoogleSignIn({
    authClient: auth,
    clientIds: {
      web: feEnvVars.GOOGLE_CLIENT_ID,
      ios: feEnvVars.GOOGLE_IOS_CLIENT_ID,
      android: feEnvVars.GOOGLE_ANDROID_CLIENT_ID,
    },
    enabled: enableGoogleLogin,
    onError: error => { tel.error(error, { operation: 'login.google' }) },
    onSuccess: () => { navigate({ to: '/' }) },
  })
  const isLoading = emailMagicLinkMutation.isPending
  const error =
    authError ??
    googleSignIn.error ??
    emailMagicLinkMutation.error ??
    null

  async function continueWithEmail() {
    const nextEmail = email.trim()
    if (!nextEmail) {
      emailMagicLinkMutation.reset()
      setAuthError(new Error(t('emailRequired')))
      return
    }

    setAuthError(null)
    setEmailSentTo(null)
    emailMagicLinkMutation.reset()

    try {
      await emailMagicLinkMutation.mutateAsync(nextEmail)
      setEmailSentTo(nextEmail)
    } catch (error) {
      setAuthError(error)
    }
  }

  function collapseEmailForm() {
    if (emailSentTo || isLoading) return
    setShowEmailForm(false)
    setAuthError(null)
    setEmailSentTo(null)
    emailMagicLinkMutation.reset()
  }

  function collapseEmailFormWhenFocusLeaves(
    nextFocusedElement: EventTarget | null,
  ) {
    if (
      nextFocusedElement !== null &&
      (nextFocusedElement === emailInputRef.current || nextFocusedElement === emailSubmitRef.current)
    ) {
      return
    }
    collapseEmailForm()
  }

  useEffect(() => {
    // A local log, not `tel.audit`: audit logs reach the server, which only accepts issue tracking and APM
    // (flashcards-telemetry.requirements.md 2026-11-01).
    tel.info('Login page opened', { operation: 'login.view' })
  }, [tel])

  useEffect(() => {
    // flashcards-local-first.spec.md: /login stays open while anonymous; only a stored account leaves it.
    if (auth.isLoggedIn()) {
      navigate({ to: '/' })
    }
  }, [auth, navigate])

  useEffect(() => {
    if (!(showEmailForm && shouldFocusEmailRef.current)) return
    emailInputRef.current?.focus()
    shouldFocusEmailRef.current = false
  }, [showEmailForm])

  return (
    <main className="bg-surface-0 text-on-surface relative flex min-h-dvh p-8">
      <IconButton
        className="absolute top-4 right-4"
        semanticRole="secondary"
        type="button"
        variant="text"
        onClick={() => {
          navigate({ to: '/settings' })
        }}
      >
        <SettingsIcon />
        {t('settings')}
      </IconButton>

      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-12">
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 text-center">
            <Image
              src="https://osnovahq.com/pwa/pwa-192x192.png"
              alt=""
              className="size-24 rounded-3xl"
              noThumb
            />
            <div className="flex flex-col gap-1">
              <p className="text-on-surface-secondary text-xl">
                {t('continueTo')}
              </p>
              <h1 className="text-primary text-5xl font-semibold tracking-tight">
                Flashcards
              </h1>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {isLoading && !showEmailForm ? (
            <p className="text-on-secondary text-center text-sm">
              {t('googleLoading')}
            </p>
          ) : null}
          {error ? (
            <ErrorDetails error={error} compact />
          ) : null}
          <GoogleSignInButton
            className="w-full"
            label={t('google')}
            disabled={isLoading || !googleSignIn.ready}
            signIn={googleSignIn.signIn}
          />
          {showEmailForm ? null : (
            <Button
              type="button"
              size="lg"
              variant="outlined"
              disabled={isLoading}
              data-testid="login-email-button"
              onClick={() => {
                shouldFocusEmailRef.current = true
                setShowEmailForm(true)
              }}
            >
              <MailIcon />
              {t('email')}
            </Button>
          )}
          {showEmailForm ? (
            <div className="pt-4">
              <Form
                className="gap-3"
                onSubmit={(event) => {
                  event.preventDefault()
                  void continueWithEmail()
                }}
              >
                <Field
                  name="email"
                  validate={(value) =>
                    typeof value === 'string' && value.trim()
                      ? null
                      : t('emailRequired')
                  }
                >
                  <FieldControl
                    ref={emailInputRef}
                    render={
                      <Input
                        type="email"
                        aria-required="true"
                        value={email}
                        placeholder={t('emailPlaceholder')}
                        data-testid="login-email-input"
                        onBlur={(event) => {
                          collapseEmailFormWhenFocusLeaves(event.relatedTarget)
                        }}
                        onChange={(event) => {
                          setEmail(event.target.value)
                          setAuthError(null)
                          emailMagicLinkMutation.reset()
                        }}
                      />
                    }
                  />
                  <FieldError data-testid="login-email-error" />
                </Field>
                {emailSentTo ? (
                  <p
                    className="text-success text-sm leading-6"
                    data-testid="login-email-sent"
                  >
                    {t('magicLinkSent', { email: emailSentTo })}
                  </p>
                ) : null}
                <Button
                  ref={emailSubmitRef}
                  type="submit"
                  variant="filled"
                  loading={isLoading}
                  onBlur={(event) => {
                    collapseEmailFormWhenFocusLeaves(event.relatedTarget)
                  }}
                >
                  {t('continueWithEmail')}
                </Button>
              </Form>
            </div>
          ) : null}
        </div>
      </div>
      <OtaStatus />
    </main>
  )
}
